import Decimal from "decimal.js";
import paymentDTO from "../../redux/payment/dto/payment";
import {getPaymentTypeByReasonCode, getProductsTotalSum} from "../selectors";

export const getFirstStartedGiftCardPayment = (payments) => {
    const startedPayments = payments.filter(function (payment) {
        return payment.type === "GIFTCARD" && payment.status === "started";
    });
    return startedPayments.length > 0 ? startedPayments[0] : false;
};

export const getCheckedGiftCardPayments = (payments) => {
    return payments.filter(function (payment) {
        return payment.type === "GIFTCARD" && payment.status === "checked";
    });
};

export const giftCardPaymentIsPresent = (payments) => {
    return typeof payments.find(payment => {
        return payment.type === "GIFTCARD";
    }) !== "undefined";
}

export const giftCardIsInTransaction = (number, payments) => {
    return payments.filter(function (payment) {
        return payment.type === "GIFTCARD" && payment.status === "checked" && payment.giftCard.cardNumber === number;
    }).length > 0;
};

export const getGiftCardsSum = payments => {
    let total = getCheckedGiftCardPayments(payments).reduce((acc, payment) => {
        acc = acc.plus(new Decimal(payment.sum));
        return acc;
    }, new Decimal(0));

    return total.toDecimalPlaces(4).toString();
};

export const getToBeCreditedSum = (state) => {
    const totalSum = state.rootReducer.invoice !== false ? state.rootReducer.invoice.total : getProductsTotalSum(state.rootReducer.productsInBasket);
    const remainingSum = new Decimal(totalSum).minus(new Decimal(state.payment.totalPaid)).toDecimalPlaces(4).toString();
    const giftCardsSum = getGiftCardsSum(state.payment.payments);
    return new Decimal(remainingSum).minus(new Decimal(giftCardsSum)).toDecimalPlaces(4).toString();
}

export const getCashPayment = (payments) => {
    let cashPayments = payments.filter(function (payment) {
        return payment.type === "CASH";
    });

    return cashPayments.length > 0 ? cashPayments[0] : false;
};

export const getCardPayments = (payments) => {
    return payments.filter(function (payment) {
        return payment.type === "CARD";
    });
};

export const getTotalPaid = (payments) => {
    return payments.reduce(function (acc, payment) {
        acc += payment.sumPaid - payment.sumChange;
        return acc;
    }, 0);
};

export const getCashChange = (payments) => {
    return payments.reduce(function (acc, payment) {
        acc = acc.plus(new Decimal(payment.cashChange));
        return acc;
    }, new Decimal(0)).toDecimalPlaces(4).toString();
};

export const getPayment = (payment) => {
    return Object.assign(new paymentDTO(), structuredClone(payment));
}

export const getManualCardPayment = (paymentSum, state) => {
    let paymentType = getManualCardPaymentType();
    if(paymentType === false){
        return false;
    }
    let erplyPaymentType = getPaymentTypeByReasonCode(state.payment.paymentTypes, paymentType.reasonCode);
    if(typeof erplyPaymentType === "undefined"){
        return false;
    }

    const paymentsLength = state.payment.payments.length;
    const nextPaymentIndex = paymentsLength === 0 ? 0 : state.payment.payments[paymentsLength - 1].index + 1;
    const payment = new paymentDTO(nextPaymentIndex, paymentSum, "success", paymentType.type);
    payment.title = paymentType.name;
    payment.typeID = erplyPaymentType.id;
    payment.sumPaid = paymentSum;
    return payment;
}

export const getManualCardPaymentType = () => {
    let paymentType = window.AppConf.paymentTypes.manualInput.find(el => el.reasonCode === "98");
    return typeof paymentType === "undefined" ? false : paymentType;
}

export const getAllCardPaymentTypesParts = (conf) => {
    let parameterName = 'DKTCardPaymentTypes';
    let counter = 0;
    let name = parameterName;
    let value = "";
    while(typeof conf[name] !== "undefined"){
        value += conf[name];
        if(conf[name].length < 1500){
            break;
        }
        counter++;
        name = parameterName + counter;
    }
    if(value !== ""){
        return JSON.parse(value);
    }
    return [];
}

export const paymentTypeIsOnPoslog = (availableTenderLines, paymentType) => {
    return typeof availableTenderLines.find(el => el.tender.typeCode === "Sale" && el.tender.reasonCode.value === paymentType.reasonCode) !== "undefined";
}

export const posLogHasGiftCardMOP = (availableTenderLines) => {
    return typeof availableTenderLines.find(el => el.tender.typeCode === "Sale" && typeof el.tender.giftCard !== "undefined") !== "undefined";
}

export const returnBasePosLogIsWebOrder = (state) => {
    return typeof state.rootReducer.returnBasePOSLog.transaction?.retailTransaction?.specialOrderNumber !== 'undefined';
}

export const getMainPaymentTypeLabel = (type, defaultValue) => {
    let label = window.AppConf.paymentTypes?.main?.[type]?.label ?? "";
    return label === "" ? defaultValue : label;
}

export const getPaymentTypeRoundToNearest = (paymentSum, paymentType) => {
    let roundToNearest = paymentType?.roundToNearest ?? false;
    if(roundToNearest !== false && window.AppConf.preventPaymentRoundingForAmountLessThanRounding && Math.abs(paymentSum) < paymentType.roundToNearest){
        roundToNearest = false;
    }
    return roundToNearest;
}

export const getBlockedTransactionPaymentType = (blockedTypes, type) => {
    let id = Object.keys(blockedTypes).find((key) => {
        if(typeof blockedTypes[key] === "undefined"){
            return false;
        }
        return blockedTypes[key].paymentTypes.indexOf(type) !== -1;
    });
    return typeof id !== "undefined" ? blockedTypes[id] : false;
}

export const getPaymentReasonCode = (payment, paymentTypes) => {
    if(payment.type === "GIFTCARD" && typeof payment?.giftCard?.meanOfPayment !== "undefined"){
        return payment.giftCard.meanOfPayment;
    }
    let paymentType = paymentTypes.find(el => {
        if(typeof payment.typeID !== "undefined"){
            return parseInt(el.id) === parseInt(payment.typeID);
        }
        return payment.type === el.type;
    });
    if(typeof paymentType !== "undefined"){
        return paymentType.quickBooksDebitAccount;
    }
    return false;
}

export const getGiftCardPaymentReasonCodes = (paymentTypes) => {
    return paymentTypes.reduce((acc, el) => {
        if(el.type === "GIFTCARD"){
            acc.push(el.quickBooksDebitAccount);
        }
        return acc;
    }, []);
}
export const getPaymentsReasonCodes = (payments, paymentTypes) => {
    return payments.reduce((acc, payment) => {
        let reasonCode = getPaymentReasonCode(payment, paymentTypes);
        if(reasonCode !== false){
            acc.push(reasonCode);
        }
        return acc;
    }, []);
}